import React, { useState } from "react";

import { useQuery } from "@apollo/client";
import {
  DeleteForever as DeleteForeverIcon,
  Edit as EditIcon,
  MoreHoriz as MoreHorizIcon,
  Warning as WarningIcon,
  ContentCopy,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Portal,
  Typography,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import Dialog from "@/components/Dialog";
import Padding from "@/components/layout/Padding";
import { graphql } from "@/gql";
import useDialogState from "@/hooks/useDialogState";
import { colors } from "@/theme/overrides/colors";

const useStyles = makeStyles()({
  deleteDialogPaper: {
    minWidth: 400,
    maxWidth: 400,
  },
  deleteDialogContent: {
    paddingTop: 0,
  },
  deleteDialogIcon: {
    fontSize: 60,
    color: colors.secondary,
  },
  deleteDialogTitle: {
    fontWeight: 700,
    fontSize: 28,
    lineHeight: 1,
    color: colors.dark,
    textAlign: "center",
  },
  deleteDialogMessage: {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "22px",
    color: colors.grey,
    textAlign: "center",
  },
  deleteDialogButton: {
    minWidth: 125,
  },
  itemIcon: {
    minWidth: 30,
  },
  mainText: {
    color: colors.primary,
  },
  deleteText: {
    color: "#FF6262",
  },
});

type OptionMenuProps = {
  templateId: string;
  ownerId: string;
  onDelete: () => Promise<void>;
  onDuplicate: () => Promise<void>;
};

const optionMenuQuery = graphql(`
  query TemplateOptionMenu {
    coachViewer {
      id
    }
  }
`);

const OptionMenu: React.FC<OptionMenuProps> = ({
  templateId,
  ownerId,
  onDelete,
  onDuplicate,
}) => {
  const { classes } = useStyles();
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const deleteDialog = useDialogState();
  const [isDeleting, setIsDeleting] = useState(false);

  const { data } = useQuery(optionMenuQuery);
  const coachId = data?.coachViewer.id;

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const closeDeleteDialog = () => {
    setIsDeleting(false);
    deleteDialog.hide();
  };

  const handleDeleteConfirm = () => {
    setIsDeleting(true);
    onDelete().then(closeDeleteDialog);
  };

  return (
    <Box display="flex" flexDirection="row">
      <IconButton onClick={handleOpen} size="large">
        <MoreHorizIcon />
      </IconButton>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {coachId == ownerId
          ? [
              <>
                <MenuItem
                  component={Link}
                  to={`${pathname}/${templateId}/edit`}
                  onClick={handleClose}
                >
                  <ListItemIcon className={classes.itemIcon}>
                    <EditIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="body2" className={classes.mainText}>
                    Edit
                  </Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleClose();
                    onDuplicate();
                  }}
                >
                  <ListItemIcon className={classes.itemIcon}>
                    <ContentCopy color="primary" fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="body2" className={classes.mainText}>
                    Duplicate
                  </Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleClose();
                    deleteDialog.show();
                  }}
                >
                  <ListItemIcon className={classes.itemIcon}>
                    <DeleteForeverIcon
                      className={classes.deleteText}
                      fontSize="small"
                    />
                  </ListItemIcon>
                  <Typography variant="body2" className={classes.deleteText}>
                    Delete
                  </Typography>
                </MenuItem>
              </>,
            ]
          : []}
      </Menu>

      <Portal>
        <Dialog
          title="Delete Template"
          open={deleteDialog.shown}
          onClose={deleteDialog.hide}
          classes={{ paper: classes.deleteDialogPaper }}
          contentClassName={classes.deleteDialogContent}
          content={
            <Box margin="8px" alignItems="center">
              <Box
                margin={9}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <div className={classes.deleteDialogIcon}>
                  <WarningIcon fontSize="inherit" color="inherit" />
                </div>
                <Typography className={classes.deleteDialogTitle}>
                  Are you sure?
                </Typography>
              </Box>
              <Padding>
                <Typography className={classes.deleteDialogMessage}>
                  Do you really want to delete this template?
                </Typography>
              </Padding>
            </Box>
          }
          actions={
            <Button
              size="large"
              className={classes.deleteDialogButton}
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? <CircularProgress size="1.6rem" /> : <>Confirm</>}
            </Button>
          }
        />
      </Portal>
    </Box>
  );
};

export default OptionMenu;
